import { useCallback, useRef } from 'react';
import type { SyntheticEvent } from 'react';

// テキストエリアの自動リサイズ + キャレット表示フック（本文エディタ用）
// マルチラインテキスト編集時に、サイズ変更・スクロール動作の一貫性を保つ。

type UseTextareaAutoResizeParams = {
  minRows: number;
  lineHeightRatio: number;
};

const DEFAULT_FONT_SIZE = 16;

export const useTextareaAutoResize = ({
  minRows,
  lineHeightRatio,
}: UseTextareaAutoResizeParams) => {
  const caretMirrorMapRef = useRef(new WeakMap<HTMLTextAreaElement, HTMLDivElement>());
  const caretRafMapRef = useRef(new WeakMap<HTMLTextAreaElement, number>());
  const manualMinHeightMapRef = useRef(new WeakMap<HTMLTextAreaElement, number>());
  const resizeStartHeightMapRef = useRef(new WeakMap<HTMLTextAreaElement, number>());
  const lastAutoHeightMapRef = useRef(new WeakMap<HTMLTextAreaElement, number>());

  const getBaselineMinHeight = useCallback(
    (el: HTMLTextAreaElement): number => {
      const style = window.getComputedStyle(el);
      const minHeightFromCss = parseFloat(style.minHeight);
      if (Number.isFinite(minHeightFromCss) && minHeightFromCss > 0) {
        return minHeightFromCss;
      }

      const fontSize = parseFloat(style.fontSize);
      const rawLineHeight = style.lineHeight;
      let resolvedLineHeight =
        Number.isFinite(fontSize) && fontSize > 0
          ? fontSize * lineHeightRatio
          : DEFAULT_FONT_SIZE * lineHeightRatio;

      if (rawLineHeight && rawLineHeight !== 'normal') {
        if (rawLineHeight.endsWith('px')) {
          const px = parseFloat(rawLineHeight);
          if (Number.isFinite(px) && px > 0) resolvedLineHeight = px;
        } else {
          const unitless = parseFloat(rawLineHeight);
          if (Number.isFinite(unitless) && unitless > 0) {
            if (Number.isFinite(fontSize) && fontSize > 0) {
              resolvedLineHeight = unitless * fontSize;
            } else {
              resolvedLineHeight = unitless * DEFAULT_FONT_SIZE;
            }
          }
        }
      }

      const paddingTop = parseFloat(style.paddingTop) || 0;
      const paddingBottom = parseFloat(style.paddingBottom) || 0;
      return resolvedLineHeight * minRows + paddingTop + paddingBottom;
    },
    [lineHeightRatio, minRows]
  );

  const autoResizeTextarea = useCallback(
    (el: HTMLTextAreaElement | null) => {
      if (!el) return;

      let minHeight = getBaselineMinHeight(el);
      const prevHeight = el.offsetHeight;
      const lastAutoHeight = lastAutoHeightMapRef.current.get(el);
      if (typeof lastAutoHeight === 'number' && Math.abs(prevHeight - lastAutoHeight) >= 2) {
        manualMinHeightMapRef.current.set(el, prevHeight);
        el.style.minHeight = `${prevHeight}px`;
      }

      if (minHeight <= 0) {
        minHeight = getBaselineMinHeight(el);
      }

      const manualMinHeight = manualMinHeightMapRef.current.get(el) ?? 0;
      if (manualMinHeight > 0) {
        minHeight = Math.max(minHeight, manualMinHeight);
      }
      el.style.minHeight = `${minHeight}px`;

      el.style.setProperty('overflow-y', 'hidden', 'important');
      el.style.setProperty('max-height', 'none', 'important');

      const prevScrollTop = el.scrollTop;
      el.style.setProperty('height', 'auto', 'important');
      const nextHeight = Math.max(el.scrollHeight, minHeight);
      el.style.setProperty('height', `${nextHeight}px`, 'important');
      lastAutoHeightMapRef.current.set(el, nextHeight);

      if (prevScrollTop > 0 && el.scrollTop !== prevScrollTop) {
        el.scrollTop = prevScrollTop;
      }
    },
    [getBaselineMinHeight]
  );

  const getCaretMirror = useCallback(
    (el: HTMLTextAreaElement, style: CSSStyleDeclaration): HTMLDivElement => {
      let mirror = caretMirrorMapRef.current.get(el);
      if (!mirror) {
        mirror = document.createElement('div');
        mirror.setAttribute('aria-hidden', 'true');
        mirror.style.position = 'fixed';
        mirror.style.top = '0';
        mirror.style.left = '-9999px';
        mirror.style.visibility = 'hidden';
        mirror.style.whiteSpace = 'pre-wrap';
        mirror.style.wordWrap = 'break-word';
        mirror.style.overflow = 'hidden';
        document.body.appendChild(mirror);
        caretMirrorMapRef.current.set(el, mirror);
      }

      mirror.style.boxSizing = style.boxSizing;
      mirror.style.width = `${el.offsetWidth}px`;
      mirror.style.padding = style.padding;
      mirror.style.border = style.border;
      mirror.style.fontFamily = style.fontFamily;
      mirror.style.fontSize = style.fontSize;
      mirror.style.fontWeight = style.fontWeight;
      mirror.style.fontStyle = style.fontStyle;
      mirror.style.letterSpacing = style.letterSpacing;
      mirror.style.lineHeight = style.lineHeight;
      mirror.style.textTransform = style.textTransform;
      mirror.style.wordBreak = style.wordBreak;
      mirror.style.overflowWrap = style.overflowWrap;

      return mirror;
    },
    []
  );

  const scrollCaretIntoView = useCallback(
    (el: HTMLTextAreaElement | null) => {
      if (!el || !document.body || !el.isConnected) return;

      const style = window.getComputedStyle(el);
      const mirror = getCaretMirror(el, style);
      const value = el.value ?? '';
      const pos = typeof el.selectionEnd === 'number' ? el.selectionEnd : value.length;

      mirror.textContent = value.slice(0, pos);
      const marker = document.createElement('span');
      marker.textContent = '\u200b';
      mirror.appendChild(marker);

      const lineHeight = (() => {
        const lh = parseFloat(style.lineHeight);
        if (Number.isFinite(lh) && lh > 0) return lh;
        const fs = parseFloat(style.fontSize);
        return Number.isFinite(fs) && fs > 0
          ? fs * lineHeightRatio
          : DEFAULT_FONT_SIZE * lineHeightRatio;
      })();

      const rect = el.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset || 0;
      const caretTop = rect.top + scrollY + marker.offsetTop - (el.scrollTop || 0);
      const caretBottom = caretTop + lineHeight;

      const viewportTop = scrollY;
      const viewportBottom = scrollY + window.innerHeight;
      const margin = 16;

      if (caretTop < viewportTop + margin) {
        window.scrollTo({ top: Math.max(caretTop - margin, 0), behavior: 'auto' });
      } else if (caretBottom > viewportBottom - margin) {
        window.scrollTo({ top: caretBottom - window.innerHeight + margin, behavior: 'auto' });
      }
    },
    [getCaretMirror, lineHeightRatio]
  );

  const scheduleScrollCaretIntoView = useCallback(
    (el: HTMLTextAreaElement | null) => {
      if (!el) return;
      const prev = caretRafMapRef.current.get(el);
      if (typeof prev === 'number') cancelAnimationFrame(prev);
      const id = requestAnimationFrame(() => {
        caretRafMapRef.current.delete(el);
        scrollCaretIntoView(el);
      });
      caretRafMapRef.current.set(el, id);
    },
    [scrollCaretIntoView]
  );

  const getTextareaFromEvent = useCallback(
    (e: SyntheticEvent): HTMLTextAreaElement | null => {
      const target = e.target as HTMLElement | null;
      if (target && target.tagName === 'TEXTAREA') {
        return target as HTMLTextAreaElement;
      }
      const current = e.currentTarget as HTMLElement | null;
      return current ? (current.querySelector('textarea') as HTMLTextAreaElement | null) : null;
    },
    []
  );

  const finalizeManualResize = useCallback(
    (el: HTMLTextAreaElement | null) => {
      if (!el) return;
      const start = resizeStartHeightMapRef.current.get(el);
      resizeStartHeightMapRef.current.delete(el);
      const end = el.offsetHeight;
      if (typeof start === 'number' && Math.abs(end - start) >= 2) {
        const baseline = getBaselineMinHeight(el);
        const nextMinHeight = Math.max(end, baseline);
        manualMinHeightMapRef.current.set(el, nextMinHeight);
        el.style.minHeight = `${nextMinHeight}px`;
        if (end < baseline) {
          el.style.height = `${baseline}px`;
        }
      }
    },
    [getBaselineMinHeight]
  );

  const handleResizeStart = useCallback(
    (e: SyntheticEvent) => {
      const el = getTextareaFromEvent(e);
      if (!el) return;
      resizeStartHeightMapRef.current.set(el, el.offsetHeight);
      const onUp = () => finalizeManualResize(el);
      window.addEventListener('pointerup', onUp, { once: true, capture: true });
      window.addEventListener('mouseup', onUp, { once: true, capture: true });
    },
    [finalizeManualResize, getTextareaFromEvent]
  );

  const handleResizeEnd = useCallback(
    (e: SyntheticEvent) => {
      finalizeManualResize(getTextareaFromEvent(e));
    },
    [finalizeManualResize, getTextareaFromEvent]
  );

  return {
    autoResizeTextarea,
    scheduleScrollCaretIntoView,
    getTextareaFromEvent,
    handleResizeStart,
    handleResizeEnd,
  };
};
